import { useAuth } from "@clerk/clerk-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef } from "react";
import type { AssetResponse } from "../generated/api/types.gen";
import { useAssetModalState } from "../hooks/useAssetModalState";
import { useImageAssets } from "../hooks/useImageAssets";
import { useModalCommitGuard } from "../hooks/useModalCommitGuard";
import { createImageAssetForProject } from "../lib/asset-create";
import { EMPTY_MESSAGE, TAB_LABEL } from "../lib/asset-modal-tabs";
import { fileNameStem, readFileAsDataUrlAsync } from "../lib/image-input";
import { galleryTreeQuery } from "../lib/queries";
import type { ReferenceAttachment } from "../lib/reference-attachment";
import { AssetBrowserTab } from "./AssetBrowserTab";
import { AssetModalChrome } from "./AssetModalChrome";
import { AssetPreviewLightbox } from "./AssetPreviewLightbox";
import { ImageAssetUploadFields } from "./ImageAssetUploadFields";
import { SaveAsAssetCheckbox } from "./SaveAsAssetCheckbox";

type ReferenceImageTab = "assets" | "image";

const TABS = [
  { id: "assets" as ReferenceImageTab, label: TAB_LABEL.assets },
  { id: "image" as ReferenceImageTab, label: TAB_LABEL.image },
];

interface ReferenceImageModalProps {
  isOpen: boolean;
  /** Set when a paste or drop should open on the upload tab, already filled. */
  initialFile?: File | null;
  /** Project the design belongs to; a saved asset is filed under it. */
  projectId?: string | null;
  onSelect: (attachment: ReferenceAttachment) => void;
  onClose: () => void;
}

/** Project names only exist in the gallery tree. While it loads, the
 *  save-as-asset label stays generic. */
function useFolderName(isOpen: boolean, projectId?: string | null) {
  const { orgId } = useAuth();
  const { data } = useQuery({
    ...galleryTreeQuery(orgId),
    enabled: isOpen && projectId != null,
  });
  if (!projectId) return null;
  return (
    data?.projects?.find((project) => project.id === projectId)?.name ?? null
  );
}

function attachmentFromAsset(asset: AssetResponse): ReferenceAttachment {
  return {
    previewUrl: asset.value,
    file: null,
    assetId: asset.id,
    assetName: asset.name,
  };
}

function useReferenceUpload(args: {
  isOpen: boolean;
  isImageTab: boolean;
  file: File | null;
  name: string;
  saveAsAsset: boolean;
  projectId?: string | null;
  onSelect: (attachment: ReferenceAttachment) => void;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const guard = useModalCommitGuard({
    isOpen: args.isOpen,
    isActiveTab: args.isImageTab,
  });
  const created = useRef<AssetResponse | null>(null);
  const createdFrom = useRef<File | null>(null);
  if (createdFrom.current !== args.file) {
    createdFrom.current = args.file;
    created.current = null;
  }

  return useMutation({
    mutationFn: async (): Promise<ReferenceAttachment> => {
      guard.beginAttempt();
      const file = args.file;
      if (!file) throw new Error("No file selected");
      const previewUrl = await readFileAsDataUrlAsync(file);
      if (!args.saveAsAsset) {
        return { previewUrl, file, assetId: null, assetName: null };
      }
      const asset = await createImageAssetForProject(queryClient, {
        file,
        name: args.name.trim(),
        projectId: args.projectId,
        created,
      });
      return {
        previewUrl,
        file,
        assetId: asset.id,
        assetName: asset.name,
      };
    },
    onSuccess: (attachment) => {
      if (!guard.shouldCommit()) return;
      args.onSelect(attachment);
      args.onClose();
    },
  });
}

/** Pick a saved image, or upload one, to attach as a generation reference.
 *  Saving the upload is optional — an unsaved image is still attached. */
export function ReferenceImageModal({
  isOpen,
  initialFile = null,
  projectId,
  onSelect,
  onClose,
}: ReferenceImageModalProps) {
  const seedFile = isOpen ? initialFile : null;
  const state = useAssetModalState<ReferenceImageTab>(
    isOpen,
    seedFile ? "image" : "assets",
    { file: seedFile, name: seedFile ? fileNameStem(seedFile.name) : "" },
  );
  const isAssetsTab = state.activeTab === "assets";
  const imageAssets = useImageAssets(isOpen && isAssetsTab);
  const folderName = useFolderName(isOpen, projectId);
  const commit = useReferenceUpload({
    isOpen,
    isImageTab: !isAssetsTab,
    file: state.file,
    name: state.name,
    saveAsAsset: state.saveAsAsset,
    projectId,
    onSelect,
    onClose,
  });

  if (!isOpen) return null;

  const selectedAsset =
    imageAssets.assets.find((asset) => asset.id === state.selectedAssetId) ??
    null;
  const imageNeedsName = state.saveAsAsset && state.name.trim().length === 0;
  const okDisabled =
    commit.isPending ||
    (isAssetsTab ? selectedAsset === null : !state.file || imageNeedsName);

  const handleSelectFile = (file: File | null) => {
    state.setFile(file);
    if (file && !state.name.trim()) state.setName(fileNameStem(file.name));
  };

  const handleOk = () => {
    if (okDisabled) return;
    if (isAssetsTab) {
      if (!selectedAsset) return;
      onSelect(attachmentFromAsset(selectedAsset));
      onClose();
      return;
    }
    commit.mutate();
  };

  return (
    <>
      <AssetModalChrome
        ariaContext="add reference image"
        title="Add Reference Image"
        subtitle="Choose a saved asset or upload an image."
        tabs={TABS}
        activeTab={state.activeTab}
        onTabChange={state.setActiveTab}
        footerLeft={
          isAssetsTab ? undefined : (
            <SaveAsAssetCheckbox
              checked={state.saveAsAsset}
              onChange={state.setSaveAsAsset}
              label={
                folderName
                  ? `Save image to ${folderName}`
                  : "Save image as asset"
              }
            />
          )
        }
        primary={{
          label: commit.isPending ? "Adding…" : "Add",
          disabled: okDisabled,
          onClick: handleOk,
        }}
        onClose={onClose}
      >
        {isAssetsTab ? (
          <AssetBrowserTab
            query={imageAssets}
            projectId={projectId}
            enabled={isOpen && isAssetsTab}
            selectedAssetId={state.selectedAssetId}
            onSelect={(asset) => state.setSelectedAssetId(asset.id)}
            onPreview={state.setPreviewAsset}
            emptyMessage={EMPTY_MESSAGE.imageAsset}
          />
        ) : (
          <>
            <ImageAssetUploadFields
              file={state.file}
              previewUrl={state.previewUrl}
              onSelectFile={handleSelectFile}
              name={state.name}
              onNameChange={state.setName}
              onSubmit={handleOk}
            />
            {commit.isError && (
              <p className="mt-4 text-sm text-red-400" role="alert">
                Couldn&apos;t use this image. Please try again.
              </p>
            )}
          </>
        )}
      </AssetModalChrome>
      <AssetPreviewLightbox
        asset={state.previewAsset}
        onClose={() => state.setPreviewAsset(null)}
        onUse={(asset) => {
          state.setSelectedAssetId(asset.id);
          state.setPreviewAsset(null);
        }}
      />
    </>
  );
}
